import React from 'react';
import {
  useLocation,
  useHistory,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Select } from 'components/FormFields';
import PageHeaderIcon from 'components/PageHeaderIcon';
import { useGetAdminTopics, useGetAdminVideos } from './gql';
import SettingsPage from './pages/SettingsPage';
import TopicsPage from './pages/TopicsPage';
import VideosPage from './pages/VideosPage';
import {
  Container,
  HeaderContainer,
  HeaderContent,
  Title,
  Subtitle,
  TabContainer,
  Badge,
  ContentWrapper,
  StatsContainer,
  MiniStatCard,
  MiniStatValue,
  MiniStatLabel,
} from './StyledComponents';

const DesktopOnly = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const LearningHubAdmin = () => {
  const location = useLocation();
  const history = useHistory();

  // Determine route prefix
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const basePath = `${routePrefix}/learning-hub-admin`;

  // Derive active tab from URL
  const lastSegment = pathname.split('/').pop();
  const currentTab = ['videos', 'topics', 'settings'].includes(lastSegment)
    ? lastSegment
    : 'videos';

  const navigateToTab = (tab) => {
    history.push(`${basePath}/${tab}`);
  };

  const { topics } = useGetAdminTopics();
  const { videos, count: videoCount } = useGetAdminVideos({});

  const publishedCount = videos.filter((v) => v.status === 'published').length;
  const draftCount = videos.filter((v) => v.status === 'draft').length;
  const discoverCount = videos.filter((v) => v.mode === 'discover').length;
  const howtoCount = videos.filter((v) => v.mode === 'howto').length;

  return (
    <EnhancedThemeProvider>
      <Container>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="settings" />
            <HeaderContent>
              <Title>Learning Hub Manager</Title>
              <Subtitle>Manage videos, topics, and settings</Subtitle>
            </HeaderContent>
          </HeaderContainer>
        </Card>

        {/* Tabs and Stats */}
        <Card style={{ marginBottom: '30px' }}>
          <TabContainer>
            <DesktopOnly>
              <div style={{ display: 'flex', gap: '8px' }}>
                <ButtonV2
                  variant="pill"
                  active={currentTab === 'videos'}
                  onClick={() => navigateToTab('videos')}
                >
                  Videos{' '}
                  <Badge active={currentTab === 'videos'}>{videoCount}</Badge>
                </ButtonV2>
                <ButtonV2
                  variant="pill"
                  active={currentTab === 'topics'}
                  onClick={() => navigateToTab('topics')}
                >
                  Topics{' '}
                  <Badge active={currentTab === 'topics'}>
                    {topics.length}
                  </Badge>
                </ButtonV2>
                <ButtonV2
                  variant="pill"
                  active={currentTab === 'settings'}
                  onClick={() => navigateToTab('settings')}
                >
                  Settings
                </ButtonV2>
              </div>
            </DesktopOnly>
            <MobileOnly style={{ width: '100%' }}>
              <Select
                value={currentTab}
                onChange={(e) => navigateToTab(e.target.value || 'videos')}
                options={[
                  { label: `Videos (${videoCount})`, value: 'videos' },
                  { label: `Topics (${topics.length})`, value: 'topics' },
                  { label: 'Settings', value: 'settings' },
                ]}
              />
            </MobileOnly>
            <StatsContainer>
              <MiniStatCard>
                <MiniStatValue color="#34D399">{publishedCount}</MiniStatValue>
                <MiniStatLabel>Published</MiniStatLabel>
              </MiniStatCard>
              <MiniStatCard>
                <MiniStatValue color="#94A3B8">{draftCount}</MiniStatValue>
                <MiniStatLabel>Drafts</MiniStatLabel>
              </MiniStatCard>
              <MiniStatCard>
                <MiniStatValue color="#A78BFA">{discoverCount}</MiniStatValue>
                <MiniStatLabel>Ideas & Tips</MiniStatLabel>
              </MiniStatCard>
              <MiniStatCard>
                <MiniStatValue color="#60A5FA">{howtoCount}</MiniStatValue>
                <MiniStatLabel>How To Do It</MiniStatLabel>
              </MiniStatCard>
            </StatsContainer>
          </TabContainer>
        </Card>

        {/* Content */}
        <ContentWrapper>
          <Switch>
            <Route path={`${basePath}/videos`}>
              <VideosPage routePrefix={routePrefix} />
            </Route>
            <Route path={`${basePath}/topics`}>
              <TopicsPage />
            </Route>
            <Route path={`${basePath}/settings`}>
              <SettingsPage />
            </Route>
            <Redirect from={basePath} to={`${basePath}/videos`} />
          </Switch>
        </ContentWrapper>
      </Container>
    </EnhancedThemeProvider>
  );
};

export default LearningHubAdmin;
